import { Component, OnInit, ViewChild } from '@angular/core';
import { ProductAliasService } from '../../api/services/product-alias.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectService } from '../../api/services/project.services';
import { PreferencesService } from '../../api/services/preferences.service';
import { AuthService } from '../../api/services/auth.service';
import { Worksheet } from '../../api/models/worksheet';
import { BomTableService } from '../bom-table/bom-table.service';
import { ToastrService } from 'ngx-toastr';
import { NgbTypeahead, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { merge } from 'rxjs/observable/merge';
import { Project, ProjectUserInput } from '../../api/models/project-model';
import { WorksheetProduct, OrderService } from '../services/order.service';
import { OrderCreateComponent } from '../order-create/order-create.component';
import { PaginatedRequest } from '../../api/models/paginated-request';
import { of } from 'rxjs/observable/of';
import { BomLine, BomLinesRequest } from '../../api/models/bom-line';

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss']
})
export class ProductDetailComponent implements OnInit {

  product: any;
  image: string;

  worksheetList: Worksheet[] = [];
  worksheetsByProject: Worksheet[] = [];
  projectList: Project[] = [];
  originalProjectList: Project[] = [];
  quantity: number;
  selectedWorksheet: Worksheet;
  selectedProject: any;
  createNewWorksheet = false;
  displayWorksheet = true;
  projectSelected: boolean = false;

  page = 1;
  pageSize = 20;
  //sorting is not required in this flow
  sorting: string = '';
  paginatedRequest: PaginatedRequest;
  projectUserInput: ProjectUserInput;
  currentMarket: string;

  $qtySubject: Subject<any> = new Subject();
  warningMsgPresent: boolean = false;
  warningMessagesList = [];

  backToSearch: string;
  accountNumber: string;

  showNoProductMessage: boolean = false;
  noProductMessage;

  @ViewChild('instance') instance: NgbTypeahead;
  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  constructor(
    private productAliasService: ProductAliasService,
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private preferencesService: PreferencesService,
    private authService: AuthService,
    private bomTableService: BomTableService,
    private toastr: ToastrService,
    private orderService: OrderService,
    private router: Router,
    private modalService: NgbModal
  ) { }


  selectProject(projectSelected: any) {
    this.selectedProject = projectSelected;
    let page: number = 1, pageSize: number = 2000;
    const paginatedRequest = new PaginatedRequest(page, pageSize);
    this.projectService.getWorksheetsByProject(this.selectedProject.id, false, paginatedRequest).subscribe(result => {
      this.worksheetsByProject = result['content'];
      this.projectSelected = true;
    });
  }

  formatter = (x: Project) => x.name;

  search = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(filter(() => !this.instance.isPopupOpen()));
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      switchMap(term => {
        if(term === '') {
          this.projectList = this.originalProjectList;
          this.worksheetsByProject = [];
          this.projectSelected = false;
          this.quantity = null;
          this.displayWorksheet = true;
          return of(this.projectList);
        } else {
          this.projectUserInput = new ProjectUserInput(this.authService.getUserId(), true, 'Active', term);
          this.paginatedRequest = new PaginatedRequest(this.page, this.pageSize, this.sorting);
          return this.projectService.getMyProjects(this.currentMarket, this.projectUserInput, this.paginatedRequest)
          .map(results => results.records)
          .catch( err => {
            return of([]);
          })
        }
      })
    );
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.backToSearch = params['backToSearch'];
    });

    this.route
      .params
      //.map(p => p.sku)
      .subscribe(params => {
        const productSku = params['sku'];
        this.accountNumber = params['accountNumber'];
        this.productAliasService.productDetailsV2([productSku],   this.accountNumber, '', true).subscribe(products => {
          if (products && products.length > 0) {
            this.showNoProductMessage = false;
            this.product = products[0];
            if(this.product && this.product.imageUrl) {
              this.image = this.product.imageUrl;
            }
          } else {
            this.showNoProductMessage = true;
            this.noProductMessage = ["No Product details found for SKU " + productSku];
          }
        }, error => {
          this.showNoProductMessage = true;
          this.noProductMessage = ["No Product details found for SKU " + productSku];
        });
      });

    this.preferencesService.getPreferences('USER', this.authService.getUserId()).subscribe(prefs => {
      this.currentMarket = prefs['current-market'].Value;
      this.paginatedRequest = new PaginatedRequest(this.page, this.pageSize);
      this.projectService.getProjects(this.currentMarket, this.paginatedRequest).subscribe(result => {
        this.projectList = result;
        this.originalProjectList = this.projectList;
      });
    });
    this.updateQty();
  }

  addToWorksheet() {
    this.dismissWarningMessages();
    if (this.createNewWorksheet) {
      const product = new WorksheetProduct(this.product.sku, this.quantity);
      this.orderService.saveWorksheetProduct(product, this.selectedProject.id);
      const modalRef = this.modalService.open(OrderCreateComponent, { size: 'lg' });
      modalRef.componentInstance.project = this.selectedProject;
      modalRef.componentInstance.worksheet = new Worksheet();
      modalRef.componentInstance.action = 'Create';
      modalRef.result.then(() => { }).catch(() => { });
    } else {
      this.bomTableService.addItemsV2(this.getBomLineRequest()).subscribe(bomLines => {
        if( this.selectedWorksheet.approvalStatus === 'APPROVED') {
          this.projectService.resubmitForApproval(this.selectedWorksheet.id).subscribe();
        }
        this.onUpdateSuccess();
        this.quantity = 0;
      }, err => {
        this.onUpdateError();
      });
    }
  }

  private getBomLineRequest() {
    const bomLineRequest = new BomLinesRequest();
    bomLineRequest.bomId = this.selectedWorksheet.bomId;
    bomLineRequest.worksheetId = this.selectedWorksheet.id;
    bomLineRequest.customerNumber = this.accountNumber;
    const bomLine = new BomLine();
    bomLine.partNumber = this.product.sku;
    if (this.quantity < this.product.minimumQuantity) {
      this.quantity = this.product.minimumQuantity;
    }
    bomLine.quantity = this.quantity;
    bomLine.isCableItem = this.product.isCableItem;
    bomLine.isConsignmentItem = this.product.isConsignedItem;
    bomLineRequest.bomLines = [bomLine];
    return bomLineRequest;
  }

  setWorksheet(worksheetId: string) {
    if (worksheetId === '-1') {
      this.createNewWorksheet = true;
      this.displayWorksheet = true;
    } else {
      this.createNewWorksheet = false;
      this.displayWorksheet = false;
      this.selectedWorksheet = this.worksheetsByProject.find(w => w.id === worksheetId);
    }
  }

  disabledAddToWorksheetButton(){
    if(this.projectSelected && (this.createNewWorksheet || this.selectedWorksheet) && (this.quantity > 0)){
      return false;
    } 
    return true;
  }

  dismissWarningMessages(){
    this.warningMsgPresent = false;
    this.warningMessagesList = [];
  }

  updateQty() {
    this.dismissWarningMessages();
    this.$qtySubject
      .debounceTime(1000)
      .distinctUntilChanged()
      .subscribe((data: HTMLInputElement) => {
        const input = data;
        let qty = Number(input.value);
        if(qty > 0 && qty < this.product.minimumQuantity){
          this.warningMsgPresent = true;
          this.warningMessagesList.push('The quantity was adjusted to ' + this.product.minimumQuantity + 
          ' to meet the minimum quantity required for SKU ' + this.product.sku);
          this.quantity = this.product.minimumQuantity;
        }  
      });
  }

  displayWS() {
    this.router.navigate(['/dashboard', { outlets: { 'dashboard-content': ['order-worksheet', this.selectedWorksheet.id] } }]);
  }

  getBtnText(): string {
    let text;
    this.createNewWorksheet ? text = 'Create Worksheet' : text = 'Add to Worksheet';
    return text;
  }

  private onUpdateSuccess() {
    this.toastr.success('Item added to worksheet!!!', 'Succcess!', {
      timeOut: 1000,
      positionClass: 'toast-bottom-right'
    });
  }

  private onUpdateError() {
    this.toastr.error('There was an error adding the item', 'Error!', {
      timeOut: 1000,
      positionClass: 'toast-bottom-right'
    });
  }

}



// WEBPACK FOOTER //
// /codebuild/output/src3408204168/src/bitbucket.org/tesscotds/webclient/src/app/product-detail/product-detail.component.ts